package io.horion.cv.components.preview

import androidx.compose.runtime.Composable
import io.horion.cv.api.datasource.cv.OrionCV
import io.horion.cv.utils.Colors
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

@Composable
fun CvCanadaPreview(data: OrionCV) {
    val pi = data.personalInformation
    val validPersonalizedSections =
        data.personalizedSections.filter { section ->
            section.resume.isNotBlank() ||
                    section.started.isNotBlank() ||
                    section.ended.isNotBlank() ||
                    section.description.isNotBlank()
        }

    Style {
        """
    .cv-canada-preview,
    .cv-canada-preview * {
        box-sizing: border-box;
    }

    .cv-canada-preview div,
    .cv-canada-preview span,
    .cv-canada-preview a,
    .cv-canada-preview p,
    .cv-canada-preview li,
    .cv-canada-preview h1,
    .cv-canada-preview h2,
    .cv-canada-preview h3 {
        max-width: 100%;
        min-width: 0;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
    }

    .cv-canada-preview img {
        max-width: 100%;
        height: auto;
    }
    """.trimIndent()
    }

    Div(attrs = {
        attr("class", "cv-canada-preview")

        style {
            property("width", "794px")
            property("max-width", "100%")
            property("min-width", "0")
            property("min-height", "1123px")
            property("font-family", "Georgia, serif")
            property("font-size", "11px")
            property("background", Colors.White.toString())
            property("color", Colors.Black.toString())
            property("padding", "50px 60px")
            property("box-sizing", "border-box")
            property("overflow", "hidden")
        }
    }) {
        // Poste centré en haut
        if (pi.bp.isNotBlank()) {
            Div(attrs = { style {
                property("text-align", "center")
                property("font-size", "13px")
                property("letter-spacing", "3px")
                property("text-transform", "uppercase")
                property("margin-bottom", "8px")
                property("color", Colors.Black.toString())
            }}) { Text(pi.bp) }
        }

        // Nom centré (prénom puis nom, avec âge si disponible)
        val hasName = pi.firstName.isNotBlank() || pi.lastName.isNotBlank()
        if (hasName) {
            Div(attrs = { style {
                property("text-align", "center")
                property("font-size", "14px")
                property("margin-bottom", "6px")
                property("color", Colors.CvText.toString())
            }}) {
                val age = if (pi.birth.isNotBlank()) {
                    val year = extractYear(pi.birth).toIntOrNull()
                    val currentYear = kotlin.js.Date().getFullYear()
                    if (year != null) ", ${currentYear - year}" else ""
                } else ""
                Text("${pi.firstName} ${pi.lastName}$age".trim())
            }
        }

        // Ligne de contacts centrée (liens réellement cliquables)
        val location = listOfNotNull(
            pi.city.takeIf { it.isNotBlank() },
            pi.state.takeIf { it.isNotBlank() },
            pi.country.takeIf { it.isNotBlank() }
        ).joinToString(", ")

        val contactItems = mutableListOf<Pair<String, String?>>()
        if (pi.phone.isNotBlank()) contactItems.add(pi.phone to "tel:${pi.phone}")
        if (pi.email.isNotBlank()) contactItems.add(pi.email to "mailto:${pi.email}")
        pi.siteWeb?.let { (_, url) -> if (url.isNotBlank()) contactItems.add(url to url) }
        pi.links.forEach { (_, url) -> if (url.isNotBlank()) contactItems.add(url to url) }
        if (location.isNotBlank()) contactItems.add(location to null)

        if (contactItems.isNotEmpty()) {
            ContactLine(contactItems)
        }

        // Informations complémentaires (permis, situation familiale)
        val infoParts = listOfNotNull(
            pi.drivingLicense.takeIf { it.isNotBlank() },
            pi.maritalStatus.takeIf { it.isNotBlank() }
        )
        if (infoParts.isNotEmpty()) {
            Div(attrs = { style {
                property("text-align", "center")
                property("font-size", "10px")
                property("color", Colors.CvTextMuted.toString())
                property("margin-bottom", "4px")
            }}) { Text(infoParts.joinToString("  —  ")) }
        }

        // Séparateur
        Div(attrs = { style {
            property("border-top", "1px solid ${Colors.Black}")
            property("margin", "15px 0")
        }}) {}

        // Profil (HTML riche : gras/italique/souligné/alignement/listes)
        if (!data.profil.isNullOrBlank()) {
            HtmlBlock(data.profil) {
                property("font-size", "11px")
                property("color", Colors.CvText.toString())
                property("line-height", "1.6")
                property("margin-bottom", "20px")
            }
        }

        // RUBRIQUES PERSONNALISÉES
        if (validPersonalizedSections.isNotEmpty()) {
            validPersonalizedSections.forEach { section ->
                CanadaPersonalizedSection(section)
            }
        }

        // COMPÉTENCES TECHNIQUES
        val validCompetences = data.competences.filter { it.name.isNotBlank() }
        validCompetences.forEach { comp ->
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("gap", "8px")
                    property("align-items", "flex-start")
                    property("margin-bottom", "5px")
                    property("font-size", "11px")
                }
            }) {
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("width", "0")
                        property("min-width", "0")
                        property("max-width", "100%")
                        property("font-weight", "bold")
                        property("font-style", "italic")
                        property("font-family", "Georgia, serif")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }
                }) {
                    Text(comp.name)
                }

                Div(attrs = {
                    style {
                        property("flex-shrink", "0")
                        property("max-width", "35%")
                        property("color", Colors.CvText.toString())
                        property("text-align", "right")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }
                }) {
                    Text(comp.level.name.replace("_", " "))
                }
            }
        }

        // LOGICIELS
        val validSoftware = data.software.filter { it.name.isNotBlank() }
        validSoftware.forEach { sw ->
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("gap", "8px")
                    property("align-items", "flex-start")
                    property("margin-bottom", "5px")
                    property("font-size", "11px")
                }
            }) {
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("width", "0")
                        property("min-width", "0")
                        property("max-width", "100%")
                        property("font-weight", "bold")
                        property("font-style", "italic")
                        property("font-family", "Georgia, serif")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }
                }) {
                    Text(sw.name)
                }

                Div(attrs = {
                    style {
                        property("flex-shrink", "0")
                        property("white-space", "nowrap")
                        property("color", Colors.CvText.toString())
                    }
                }) {
                    Text("★".repeat(sw.stars.coerceIn(0, 5)))
                }
            }
        }

        // EXPÉRIENCES
        val validExperiences = data.experiences.filter { it.post.isNotBlank() || it.enterprise.isNotBlank() }
        if (validExperiences.isNotEmpty()) {
            SimpleDivider()
            SimpleSection("Expériences")
            validExperiences.forEach { exp ->
                CanadaTimelineEntry(
                    title       = exp.post,
                    period      = formatPeriod(exp.started, exp.ended),
                    subtitle    = "${exp.enterprise}${if (exp.city.isNotBlank()) " — ${exp.city}" else ""}",
                    bullets     = exp.description
                )
            }
        }

        // STAGES
        val validStages = data.stages.filter { it.post.isNotBlank() || it.enterprise.isNotBlank() }
        if (validStages.isNotEmpty()) {
            SimpleSection("Stages")
            validStages.forEach { stage ->
                CanadaTimelineEntry(
                    title       = stage.post,
                    period      = formatPeriod(stage.started, stage.ended),
                    subtitle    = "${stage.enterprise}${if (stage.city.isNotBlank()) " — ${stage.city}" else ""}",
                    bullets     = if (stage.description.isNotBlank()) listOf(stage.description) else emptyList()
                )
            }
        }

        // ACTIVITÉS EXTRA-SCOLAIRES
        val validActivities = data.extracurricularActivities.filter { it.name.isNotBlank() || it.enterprise.isNotBlank() }
        if (validActivities.isNotEmpty()) {
            SimpleSection("Activités extra-scolaires")
            validActivities.forEach { activity ->
                CanadaTimelineEntry(
                    title       = activity.name,
                    period      = formatPeriod(activity.started, activity.ended),
                    subtitle    = "${activity.enterprise}${if (activity.city.isNotBlank()) " — ${activity.city}" else ""}",
                    bullets     = if (activity.description.isNotBlank()) listOf(activity.description) else emptyList()
                )
            }
        }

        // FORMATIONS
        val validFormations = data.formations.filter { it.label.isNotBlank() || it.school.isNotBlank() }
        if (validFormations.isNotEmpty()) {
            SimpleSection("Formations")
            validFormations.forEach { formation ->
                CanadaTimelineEntry(
                    title       = formation.label,
                    period      = formatPeriod(formation.started, formation.ended),
                    subtitle    = if (formation.school.isNotBlank()) "${formation.school}${if (formation.city.isNotBlank()) " — ${formation.city}" else ""}" else "",
                    bullets     = if (formation.description.isNotBlank()) listOf(formation.description) else emptyList()
                )
            }
        }

        // COURS
        val validLearns = data.learns.filter { it.label.isNotBlank() }
        if (validLearns.isNotEmpty()) {
            SimpleSection("Cours")
            validLearns.forEach { learn ->
                CanadaTimelineEntry(
                    title       = learn.label,
                    period      = formatPeriod(learn.started, learn.ended),
                    subtitle    = "",
                    bullets     = if (learn.description.isNotBlank()) listOf(learn.description) else emptyList()
                )
            }
        }

        // CERTIFICATS
        val validCertificates = data.certificates.filter { it.name.isNotBlank() }
        if (validCertificates.isNotEmpty()) {
            SimpleSection("Certificats")
            validCertificates.forEach { cert ->
                CanadaTimelineEntry(
                    title       = cert.name,
                    period      = extractYear(cert.date),
                    subtitle    = "",
                    bullets     = if (cert.description.isNotBlank()) listOf(cert.description) else emptyList()
                )
            }
        }

        // RÉALISATIONS
        val validAchievements = data.achievements.filter { it.isNotBlank() }
        validAchievements.forEach { item ->
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("gap", "5px")
                    property("font-size", "10px")
                    property("line-height", "1.4")
                    property("color", Colors.CvText.toString())
                    property("margin-bottom", "2px")
                    property("padding-left", "10px")
                    property("box-sizing", "border-box")
                }
            }) {
                Span(attrs = {
                    style {
                        property("flex-shrink", "0")
                    }
                }) {
                    Text("•")
                }

                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("width", "0")
                        property("min-width", "0")
                        property("max-width", "100%")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                        property("overflow", "hidden")
                    }
                }) {
                    HtmlInline(item)
                }
            }
        }

        // RÉFÉRENCES
        val validReferences = data.references.filter { it.name.isNotBlank() || it.enterprise.isNotBlank() }
        if (validReferences.isNotEmpty()) {
            SimpleSection("Références")
            validReferences.forEach { ref ->
                Div(
                    attrs = {
                        style { property("margin-bottom", "8px") } }
                ) {
                    Div(
                        attrs = {
                            style {
                                property("width", "100%")
                                property("max-width", "100%")
                                property("min-width", "0")
                                property("font-weight", "bold")
                                property("font-style", "italic")
                                property("font-size", "11px")
                                property("white-space", "normal")
                                property("overflow-wrap", "anywhere")
                                property("word-break", "break-word") } }
                    ) { Text(ref.name) }
                    val subtitleParts = listOfNotNull(ref.enterprise.takeIf { it.isNotBlank() }, ref.city.takeIf { it.isNotBlank() })
                    if (subtitleParts.isNotEmpty()
                    ) {
                        Div(
                            attrs = {
                                style {
                                    property("font-size", "10px");
                                    property("color", Colors.CvTextMuted.toString())
                                    property("width", "100%")
                                    property("max-width", "100%")
                                    property("min-width", "0")
                                    property("white-space", "normal")
                                    property("overflow-wrap", "anywhere")
                                    property("word-break", "break-word")
                                } }
                        ) { Text(subtitleParts.joinToString(" — ")) }
                    }
                    val contactParts2 = listOfNotNull(ref.phone.takeIf { it.isNotBlank() }, ref.email.takeIf { it.isNotBlank() })
                    if (contactParts2.isNotEmpty()) {
                        Div(
                            attrs = {
                                style {
                                    property("font-size", "10px");
                                    property("color", Colors.CvText.toString())
                                    property("width", "100%")
                                    property("max-width", "100%")
                                    property("min-width", "0")
                                    property("white-space", "normal")
                                    property("overflow-wrap", "anywhere")
                                    property("word-break", "break-word")
                                } }
                        ) { Text(contactParts2.joinToString("   •   ")) }
                    }
                }
            }
        }

        // LANGUES
        val validLanguages = data.languages.filter { it.language.name.isNotBlank() }
        if (validLanguages.isNotEmpty()) {
            SimpleSection("Langues")
            validLanguages.forEach { lang ->
                Div(attrs = { style {
                    property("display", "flex")
                    property("justify-content", "space-between")
                    property("margin-bottom", "4px")
                    property("font-size", "11px")
                }}) {
                    Div(attrs = { style {
                        property("font-weight", "bold")
                        property("font-style", "italic")
                    }}) { Text(lang.language.name.lowercase().replaceFirstChar { it.uppercase() }) }
                    Div(attrs = { style {
                        property("color", Colors.CvText.toString())
                    }}) { Text(lang.level.name.replace("_", " ")) }
                }
            }
        }

        // QUALITÉS
        val validQualities = data.qualities.filter { it.isNotBlank() }
        if (validQualities.isNotEmpty()) {
            SimpleSection("Qualités")
            Div(
                attrs = {
                    style {
                        property("width", "100%")
                        property("max-width", "100%")
                        property("min-width", "0")
                        property("font-size", "11px")
                        property("color", Colors.CvText.toString())
                        property("line-height", "1.4")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word") } }
            ) {
                Text(validQualities.joinToString(", "))
            }
        }

        // SIGNATURE
        data.signature?.let { sig ->
            if (sig.city.isNotBlank() || sig.date.isNotBlank() || sig.image.isNotBlank()) {
                Div(attrs = {
                    style {
                        property("width", "100%")
                        property("max-width", "100%")
                        property("min-width", "0")
                        property("margin-top", "20px")
                        property("text-align", "right")
                        property("overflow", "hidden")
                    }
                }) {
                    val line = listOfNotNull(
                        sig.city.takeIf { it.isNotBlank() },
                        sig.date.takeIf { it.isNotBlank() }
                    )

                    if (line.isNotEmpty()) {
                        Div(attrs = {
                            style {
                                property("width", "100%")
                                property("max-width", "100%")
                                property("font-size", "10px")
                                property("color", Colors.CvTextMuted.toString())
                                property("white-space", "normal")
                                property("overflow-wrap", "anywhere")
                                property("word-break", "break-word")
                            }
                        }) {
                            Text(line.joinToString(", "))
                        }
                    }

                    if (sig.image.isNotBlank()) {
                        Img(
                            src = sig.image,
                            attrs = {
                                style {
                                    property("display", "inline-block")
                                    property("max-width", "100%")
                                    property("height", "50px")
                                    property("margin-top", "4px")
                                    property("object-fit", "contain")
                                }
                            }
                        )
                    }
                }
            }
        }
    }
}

// ── Ligne de contact centrée avec vrais liens cliquables ──────────────────────

@Composable
private fun ContactLine(items: List<Pair<String, String?>>) {
    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("text-align", "center")
            property("font-size", "10px")
            property("line-height", "1.4")
            property("color", Colors.CvText.toString())
            property("margin-bottom", "4px")
            property("white-space", "normal")
            property("overflow-wrap", "anywhere")
            property("word-break", "break-word")
        }
    }) {
        items.forEachIndexed { index, (text, url) ->
            if (index > 0) {
                Span {
                    Text("  —  ")
                }
            }

            if (url != null) {
                A(
                    href = url,
                    attrs = {
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")

                        style {
                            property("display", "inline")
                            property("max-width", "100%")
                            property("color", Colors.CvText.toString())
                            property("text-decoration", "underline")
                            property("white-space", "normal")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }
                    }
                ) {
                    Text(text)
                }
            } else {
                Span {
                    Text(text)
                }
            }
        }
    }
}

// ── Entrée timeline générique (expériences, stages, activités, formations, cours, certificats) ──

@Composable
private fun CanadaTimelineEntry(
    title: String,
    period: String,
    subtitle: String,
    bullets: List<String>
) {
    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("margin-bottom", "10px")
            property("overflow", "hidden")
            property("box-sizing", "border-box")
        }
    }) {
        // Ligne titre + période
        Div(attrs = {
            style {
                property("display", "flex")
                property("width", "100%")
                property("max-width", "100%")
                property("min-width", "0")
                property("gap", "8px")
                property("align-items", "flex-start")
                property("box-sizing", "border-box")
            }
        }) {
            if (title.isNotBlank()) {
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("width", "0")
                        property("min-width", "0")
                        property("max-width", "100%")
                        property("font-weight", "bold")
                        property("font-style", "italic")
                        property("font-family", "Georgia, serif")
                        property("font-size", "12px")
                        property("line-height", "1.3")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }
                }) {
                    Text(title)
                }
            }

            if (period.isNotBlank()) {
                Div(attrs = {
                    style {
                        property("flex-shrink", "0")
                        property("width", "auto")
                        property("max-width", "35%")
                        property("font-size", "10px")
                        property("line-height", "1.3")
                        property("text-align", "right")
                        property("color", Colors.CvTextMuted.toString())
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }
                }) {
                    Text(period)
                }
            }
        }

        // Sous-titre
        if (subtitle.isNotBlank()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("line-height", "1.3")
                    property("color", Colors.CvTextMuted.toString())
                    property("margin-top", "3px")
                    property("margin-bottom", "5px")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(subtitle)
            }
        }

        // Descriptions
        if (bullets.isNotEmpty()) {
            bullets.forEach { item ->
                Div(attrs = {
                    style {
                        property("display", "flex")
                        property("width", "100%")
                        property("max-width", "100%")
                        property("min-width", "0")
                        property("gap", "5px")
                        property("font-size", "10px")
                        property("line-height", "1.4")
                        property("color", Colors.CvText.toString())
                        property("margin-bottom", "2px")
                        property("padding-left", "10px")
                        property("box-sizing", "border-box")
                    }
                }) {
                    Span(attrs = {
                        style {
                            property("flex-shrink", "0")
                        }
                    }) {
                        Text("•")
                    }

                    Div(attrs = {
                        style {
                            property("flex", "1")
                            property("width", "0")
                            property("min-width", "0")
                            property("max-width", "100%")
                            property("white-space", "normal")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                            property("overflow", "hidden")
                        }
                    }) {
                        HtmlInline(item)
                    }
                }
            }
        }
    }
}

// ── Helpers de section ────────────────────────────────────────────────────────

@Composable
fun SimpleSection(title: String) {
    Div(attrs = { style {
        property("font-size", "16px")
        property("font-family", "Georgia, serif")
        property("font-style", "italic")
        property("color", Colors.Black.toString())
        property("margin-bottom", "10px")
        property("margin-top", "5px")
    }}) { Text(title) }
}

@Composable
fun SimpleDivider() {
    Div(attrs = { style {
        property("border-top", "1px solid ${Colors.Black}")
        property("margin", "12px 0")
    }}) {}
}

@Composable
private fun CanadaPersonalizedSection(
    section: io.horion.cv.api.datasource.cv.Section
) {
    val displayTitle = section.title
        .ifBlank { "Rubrique personnalisée" }

    SimpleSection(displayTitle)

    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("margin-bottom", "12px")
            property("overflow", "hidden")
        }
    }) {
        if (section.resume.isNotBlank()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "11px")
                    property("font-weight", "bold")
                    property("font-style", "italic")
                    property("line-height", "1.4")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(section.resume)
            }
        }

        if (section.started.isNotBlank() || section.ended.isNotBlank()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", Colors.CvTextMuted.toString())
                    property("line-height", "1.3")
                    property("margin-top", "3px")
                    property("margin-bottom", "4px")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(
                    listOf(
                        section.started,
                        section.ended
                    )
                        .filter { it.isNotBlank() }
                        .joinToString(" — ")
                )
            }
        }

        if (section.description.isNotBlank()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", Colors.CvText.toString())
                    property("line-height", "1.5")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("overflow", "hidden")
                }
            }) {
                HtmlBlock(section.description) {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", Colors.CvText.toString())
                    property("line-height", "1.5")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }
        }
    }
}